
import React, { useMemo, useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Link } from 'react-router-dom';
import { blogPosts } from '@/data/blog';
import { Button } from "@/components/ui/button";
import { Calendar, Clock, ChevronRight } from 'lucide-react';
import { blogCategories } from '@/data/blog/types';
import { Card, CardContent } from "@/components/ui/card";

// Add debug logging
console.log('Blog page loading, posts:', blogPosts.length);

const Blog = () => {
  // State for active category filter
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  
  // Filtered blog posts based on category
  const filteredPosts = useMemo(() => {
    if (!activeCategory) return blogPosts;
    return blogPosts.filter(post => post.category === activeCategory);
  }, [activeCategory]);
  
  // Handle category selection
  const handleCategoryChange = (category: string) => {
    setActiveCategory(activeCategory === category ? null : category);
  };

  // Log filtered posts when they change
  React.useEffect(() => {
    console.log('Filtered posts:', filteredPosts.length);
  }, [filteredPosts]);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-piano-black to-gray-900 text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6">
              Klaverbloggen
            </h1>
            <p className="text-xl text-gray-300">
              Tips, råd og inspiration til din klaverrejse.
            </p>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-3">
            {blogCategories.map((category) => (
              <Button
                key={category}
                variant={activeCategory === category ? "default" : "outline"}
                className={activeCategory === category ? "bg-piano-gold hover:bg-piano-gold/90" : ""}
                onClick={() => handleCategoryChange(category)}
              >
                {category}
              </Button>
            ))}
            {activeCategory && (
              <Button 
                variant="ghost" 
                onClick={() => setActiveCategory(null)}
                className="text-gray-500"
              >
                Vis alle
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {activeCategory && (
            <h2 className="text-2xl md:text-3xl font-serif font-bold mb-8 text-center">
              {activeCategory}
            </h2>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Card key={post.slug} className="overflow-hidden transition-transform hover:-translate-y-1 flex flex-col">
                {post.image && (
                  <Link to={`/blog/${post.slug}`}>
                    <div className="h-56 overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title} 
                        className="w-full h-full object-cover transition-transform hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                  </Link>
                )}
                <CardContent className="p-6 flex-grow flex flex-col">
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span className="mr-4">{post.date}</span>
                    <Clock className="h-4 w-4 mr-1" />
                    <span>{post.readTime} læsning</span>
                  </div>
                  <div className="mb-3">
                    <span className="text-xs font-medium px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                      {post.category}
                    </span>
                  </div>
                  <h2 className="text-xl font-serif font-semibold mb-3">
                    <Link to={`/blog/${post.slug}`} className="hover:text-piano-gold">
                      {post.title}
                    </Link>
                  </h2>
                  <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="mt-auto">
                    <Button asChild variant="link" className="p-0 text-piano-gold">
                      <Link to={`/blog/${post.slug}`} className="inline-flex items-center">
                        Læs mere <ChevronRight className="h-4 w-4 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Blog;
